import React, { useState } from 'react';
import { InterviewMode, InterviewService } from '../../lib/services/interviews';

interface BulkUploadSlotsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploaded?: () => void;
}

const BulkUploadSlotsModal: React.FC<BulkUploadSlotsModalProps> = ({ isOpen, onClose, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [processing, setProcessing] = useState<boolean>(false);

  if (!isOpen) return null;

  const parseCsv = async (text: string) => {
    // Expect simple CSV with header: Title,Start,End,Mode,JoinLink,Capacity,Timezone,Location
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const header = lines[0].split(',').map(h => h.trim().toLowerCase());
    const idx = (name: string) => header.indexOf(name);
    const out: any[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(',');
      const title = cols[idx('title')]?.trim() || 'Interview slot';
      const startIso = cols[idx('start')]?.trim();
      const endIso = cols[idx('end')]?.trim();
      const mode = (cols[idx('mode')]?.trim() || 'no_link') as InterviewMode;
      const joinLink = cols[idx('joinlink')]?.trim();
      const capacity = Number(cols[idx('capacity')]?.trim() || '1');
      const timezone = cols[idx('timezone')]?.trim();
      const location = cols[idx('location')]?.trim();
      if (!startIso || !endIso) continue;
      out.push({ title, startIso, endIso, mode, joinLink, capacity, timezone, location });
    }
    return out;
  };

  const onSubmit = async () => {
    try {
      setProcessing(true);
      setError('');
      if (!file) return;
      const text = await file.text();
      const rows = await parseCsv(text);
      rows.forEach((r) => {
        InterviewService.create({
          title: r.title,
          startIso: r.startIso,
          endIso: r.endIso,
          mode: r.mode,
          joinLink: r.mode === 'in_person' ? undefined : r.joinLink || undefined,
          location: r.mode === 'in_person' ? (r.location || '') : undefined,
          capacity: r.capacity || 1,
          panelists: [],
          timezone: r.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      });
      onUploaded && onUploaded();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to process file');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-[420px] border border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Bulk Upload Slots</h3>
        <p className="text-xs text-gray-600 dark:text-gray-300 mb-4">
          Upload a CSV file with columns: Title, Start, End, Mode, JoinLink, Capacity, Timezone, Location
        </p>
        <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
        <div className="mt-4 flex gap-2">
          <button disabled={!file || processing} onClick={onSubmit} className="flex-1 text-xs px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">{processing ? 'Processing...' : 'Upload'}</button>
          <button onClick={onClose} className="flex-1 text-xs px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadSlotsModal;



